import React, { useRef, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Modal,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useUploadStudentIDMutation } from "src/redux/service/users.service";
import { useSnackbar } from "notistack";
import MainButton from "../buttons/mainbutton";
import SecondaryButton from "../buttons/secondbutton";
import theme, { backgroundColor } from "src/styles/mainstyle";

interface StudentIDModalProps {
  open: boolean;
  onClose: () => void;
  status?: "pending" | "verified" | "rejected" | "none";
  onUploadSuccess?: () => void;
}

const StudentIDModal: React.FC<StudentIDModalProps> = ({
  open,
  onClose,
  status = "none",
  onUploadSuccess,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string>("");
  const [uploadStudentID, { isLoading }] = useUploadStudentIDMutation();
  const { enqueueSnackbar } = useSnackbar();
  const mobileTheme = useTheme();
  const isDesktop = useMediaQuery(mobileTheme.breakpoints.down("sm"));

  const getStatusColor = (verificationStatus: string) => {
    switch (verificationStatus) {
      case "verified":
        return "success.main";
      case "rejected":
        return "error.main";
      case "pending":
        return "warning.main";
      default:
        return "text.secondary";
    }
  };

  const getStatusLabel = (verificationStatus: string) => {
    switch (verificationStatus) {
      case "verified":
        return "✓ Student Status Verified";
      case "rejected":
        return "✗ Verification Rejected";
      case "pending":
        return "⏳ Verification Pending";
      default:
        return "No verification submitted";
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        enqueueSnackbar("Please select an image file", { variant: "error" });
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        enqueueSnackbar("File size must be less than 5MB", {
          variant: "error",
        });
        return;
      }

      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        setPreview(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      enqueueSnackbar("Please select a file", { variant: "error" });
      return;
    }

    try {
      await uploadStudentID({ file: selectedFile }).unwrap();
      enqueueSnackbar("Student ID uploaded successfully!", {
        variant: "success",
      });
      setSelectedFile(null);
      setPreview("");
      onUploadSuccess?.();
      onClose();
    } catch (error) {
      enqueueSnackbar("Failed to upload student ID. Please try again.", {
        variant: "error",
      });
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setPreview("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: !isDesktop ? "450px" : "90vw",
            maxWidth: "500px",
            bgcolor: "white",
            borderRadius: "16px",
            boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.12)",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 4,
              py: 3,
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Student ID Verification
            </Typography>
            <IconButton
              onClick={onClose}
              disabled={isLoading}
              sx={{
                color: "#666",
                "&:hover": { backgroundColor: "#f5f5f5" },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Content */}
          <Box
            sx={{
              px: 4,
              py: 4,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {/* Info banner */}
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                bgcolor: "#fff3e0",
                border: "1px solid #ffb74d",
                borderRadius: "8px",
                px: 2,
                py: 2,
              }}
            >
              <InfoOutlinedIcon 
                sx={{ color: "#f57c00", mr: 1.5, mt: 0.2, fontSize: "20px" }}
              />
              <Typography
                variant="body2"
                sx={{ color: "#424242", lineHeight: 1.6 }}
              >
                Upload a clear image of your student ID to verify your student
                status and increase trust in the community. Your photo will be
                kept private and only used for verification purposes.
              </Typography>
            </Box>

            {/* Status badge */}
            {status !== "none" && !selectedFile && (
              <Box
                sx={{
                  p: 2,
                  border: "2px solid",
                  borderColor:
                    status === "verified"
                      ? "success.main"
                      : status === "rejected"
                        ? "error.main"
                        : "warning.main",
                  borderRadius: "8px",
                  backgroundColor:
                    status === "verified"
                      ? "rgba(76, 175, 80, 0.08)"
                      : status === "rejected"
                        ? "rgba(244, 67, 54, 0.08)"
                        : "rgba(255, 193, 7, 0.08)",
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 600, color: getStatusColor(status) }}
                >
                  {getStatusLabel(status)}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ display: "block", mt: 0.5, color: "text.secondary" }}
                >
                  Your verification status has been submitted
                </Typography>
              </Box>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              style={{ display: "none" }}
            />

            {/* Select image button */}
            {status === "none" && !selectedFile && (
              <Box
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  py: 4,
                  border: "2px dashed #e0e0e0",
                  borderRadius: "8px",
                  cursor: "pointer",
                  color: "#666",
                  "&:hover": {
                    borderColor: backgroundColor,
                    color: backgroundColor,
                    bgcolor: "#fafafa",
                  },
                  transition: "all 0.2s",
                }}
              >
                <CloudUploadIcon sx={{ fontSize: 36 }} />
                <Typography variant="body2">
                  Click to select an image
                </Typography>
              </Box>
            )}

            {/* Change image link when already submitted */}
            {status !== "none" && !selectedFile && (
              <Typography
                variant="body2"
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  textAlign: "center",
                  cursor: "pointer",
                  color: backgroundColor,
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Change image
              </Typography>
            )}

            {/* Selected file confirmation */}
            {selectedFile && (
              <Typography variant="body2" color="success.main">
                ✓ File selected: {selectedFile.name}
              </Typography>
            )}

            {/* Actions */}
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              {selectedFile && (
                <SecondaryButton onClick={handleReset} text={"Cancel"} />
              )}
              {selectedFile && (
                <MainButton
                  onClick={handleUpload}
                  disabled={isLoading}
                  isLoading={isLoading}
                  sx={{ position: "relative" }}
                  text={isLoading ? "Uploading..." : "Upload"}
                />
              )}
              {!selectedFile && (
                <SecondaryButton onClick={onClose} text={"Close"} />
              )}
            </Box>
          </Box>
        </Box>
      </Modal>
    </ThemeProvider>
  );
};

export default StudentIDModal;
